import { useState, useEffect } from 'react';
import { AdminShell } from '@/components/AdminShell';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Download } from 'lucide-react';
import { getToken } from '@/lib/auth';
import api, { API_BASE } from '@/lib/api';

export default function AdminWaitlist() {
  const [waitlist, setWaitlist] = useState([]);
  const [newsletter, setNewsletter] = useState([]);

  useEffect(() => {
    api.get('/admin/waitlist').then(res => setWaitlist(res.data)).catch(console.error);
    api.get('/admin/newsletter').then(res => setNewsletter(res.data)).catch(console.error);
  }, []);

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';

  const handleExport = (type) => {
    const token = getToken();
    window.open(`${API_BASE}/admin/export/${type}?token=${token}`, '_blank');
    // Fallback: fetch with auth header
    fetch(`${API_BASE}/admin/export/${type}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    }).then(res => res.blob()).then(blob => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${type}_export.csv`;
      a.click();
      URL.revokeObjectURL(url);
    }).catch(console.error);
  };

  return (
    <AdminShell title="Waitlist & Newsletter">
      <Tabs defaultValue="waitlist">
        <div className="flex items-center justify-between mb-6">
          <TabsList className="bg-[hsl(var(--card))] border border-[rgba(201,168,76,0.12)]">
            <TabsTrigger value="waitlist" className="font-['Inter'] text-sm data-[state=active]:bg-[rgba(27,77,68,0.3)] data-[state=active]:text-[hsl(var(--gold))]">
              Waitlist ({waitlist.length})
            </TabsTrigger>
            <TabsTrigger value="newsletter" className="font-['Inter'] text-sm data-[state=active]:bg-[rgba(27,77,68,0.3)] data-[state=active]:text-[hsl(var(--gold))]">
              Newsletter ({newsletter.length})
            </TabsTrigger>
          </TabsList>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleExport('waitlist')}
              className="border-[rgba(201,168,76,0.2)] text-[hsl(var(--silver))] hover:text-[hsl(var(--foreground))] font-['Inter'] text-xs"
            >
              <Download className="h-3.5 w-3.5 mr-1.5" /> Export Waitlist
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleExport('newsletter')}
              className="border-[rgba(201,168,76,0.2)] text-[hsl(var(--silver))] hover:text-[hsl(var(--foreground))] font-['Inter'] text-xs"
            >
              <Download className="h-3.5 w-3.5 mr-1.5" /> Export Newsletter
            </Button>
          </div>
        </div>

        <TabsContent value="waitlist">
          <div className="rounded-xl border border-[rgba(201,168,76,0.12)] overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="border-b border-[rgba(201,168,76,0.08)] hover:bg-transparent">
                  <TableHead className="font-['Inter'] text-xs font-medium uppercase tracking-[0.12em] text-[hsl(var(--silver))]">Email</TableHead>
                  <TableHead className="font-['Inter'] text-xs font-medium uppercase tracking-[0.12em] text-[hsl(var(--silver))]">Name</TableHead>
                  <TableHead className="font-['Inter'] text-xs font-medium uppercase tracking-[0.12em] text-[hsl(var(--silver))]">Joined</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {waitlist.length === 0 ? (
                  <TableRow><TableCell colSpan={3} className="text-center font-['Inter'] text-sm text-[hsl(var(--muted-foreground))] py-8">No waitlist entries yet.</TableCell></TableRow>
                ) : waitlist.map(entry => (
                  <TableRow key={entry._id} className="border-b border-[rgba(201,168,76,0.06)] hover:bg-[rgba(27,77,68,0.08)]">
                    <TableCell className="font-['Inter'] text-sm text-[hsl(var(--foreground))]">{entry.email}</TableCell>
                    <TableCell className="font-['Inter'] text-sm text-[hsl(var(--muted-foreground))]">{entry.name || '—'}</TableCell>
                    <TableCell className="font-['Inter'] text-xs text-[hsl(var(--silver))]">{formatDate(entry.created_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="newsletter">
          <div className="rounded-xl border border-[rgba(201,168,76,0.12)] overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="border-b border-[rgba(201,168,76,0.08)] hover:bg-transparent">
                  <TableHead className="font-['Inter'] text-xs font-medium uppercase tracking-[0.12em] text-[hsl(var(--silver))]">Email</TableHead>
                  <TableHead className="font-['Inter'] text-xs font-medium uppercase tracking-[0.12em] text-[hsl(var(--silver))]">Subscribed</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {newsletter.length === 0 ? (
                  <TableRow><TableCell colSpan={2} className="text-center font-['Inter'] text-sm text-[hsl(var(--muted-foreground))] py-8">No subscribers yet.</TableCell></TableRow>
                ) : newsletter.map(sub => (
                  <TableRow key={sub._id} className="border-b border-[rgba(201,168,76,0.06)] hover:bg-[rgba(27,77,68,0.08)]">
                    <TableCell className="font-['Inter'] text-sm text-[hsl(var(--foreground))]">{sub.email}</TableCell>
                    <TableCell className="font-['Inter'] text-xs text-[hsl(var(--silver))]">{formatDate(sub.subscribed_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>
    </AdminShell>
  );
}
